import { useState } from 'react'
import { SignupErrors } from '../../../types/state'
import { validateSignupPost } from '../../mfcLogic/auth'
import { safeArr, safeObj } from '../../utilities/safe'

/**
 * Internal useMyAccount state
 */
interface IMyAccountState {
    validUsername?: boolean
    validPassword?: boolean
    messages?: SignupErrors
    username?: string
    waitingForValidation?: boolean
}

/**
 * Return type of useMyAccount
 */
interface IMyAccount extends IMyAccountState {
    isValidUsername(username: string): void
    isValidPassword(password: string): void
    updateUsername(username: string): void
    updatePassword(): void
}

/**
 * Access validation functions for My Account (useMyAccount)
 * @returns IMyAccount
 */
export const useMyAccount = (): IMyAccount => {
    const [myAccountState, setMyAccountState] = useState<IMyAccountState>({
        waitingForValidation: false,
    })

    /**
     * Validate a username; upon completion sets `myAccountState.messages`
     * @param username
     * @returns boolean
     */
    const isValidUsername = (username: string) => {
        if (username && username.length >= 1) {
            try {
                validateSignupPost('username', { username }).then(
                    (response) => {
                        const { errmsg } = safeObj(response)
                        const messages = safeArr(errmsg?.username)
                        setMyAccountState({
                            messages: errmsg,
                            username: username,
                            waitingForValidation: false,
                            validUsername:
                                messages?.length < 1 || !messages
                                    ? true
                                    : false,
                        })
                    },
                )
            } catch (_ex) {
                return
            }
        }
    }

    /**
     * Verify a user's password
     * @param _password
     * @returns
     */
    const isValidPassword = (password: string) => {
        if (password && password.length >= 1) {
            try {
                validateSignupPost('new_password', {
                    new_password: password,
                }).then((response) => {
                    const { errmsg } = safeObj(response)
                    const messages = safeArr(errmsg?.password)
                    setMyAccountState({
                        messages: errmsg,
                        waitingForValidation: false,
                        validPassword:
                            messages?.length < 1 || !messages ? true : false,
                    })
                })
            } catch (_ex) {
                return
            }
        }
    }

    /**
     * Update the username in hook state
     * @param username
     */
    const updateUsername = (username: string) => {
        setMyAccountState({ username, waitingForValidation: true })
    }

    const updatePassword = () => {
        setMyAccountState({ waitingForValidation: true })
    }

    return {
        ...myAccountState,
        isValidUsername,
        isValidPassword,
        updateUsername,
        updatePassword,
    }
}
